/**
 * TenantSwitcher — header dropdown that lets a member who belongs to (or wants
 * to join) multiple tenants switch the active session OR pair into a new one.
 *
 * The dropdown always renders an "เพิ่มบริษัท" action so a single-tenant member
 * (e.g. an HR already paired to company A) has a way to enter a 2nd company's
 * pairing code — previously this was a dead-end: the /pair screen only showed
 * when the user had NO membership at all (chicken-and-egg).
 *
 * On switch we POST /me/switch-tenant, replace the JWT + profile via the auth
 * store (handled in `useSwitchTenant`), and invalidate every cached query so
 * the next render re-fetches data scoped to the new tenant. "เพิ่มบริษัท" routes
 * to /pair (reachable while authenticated) to enter a new tenant_code + code.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Building2, Plus } from 'lucide-react';
import { useAuthStore } from '../../lib/store';
import { isInLineClient } from '../../lib/liff';
import { useMyTenants, useSwitchTenant } from '../../hooks';
import { useToast } from '../ui/toast';

export function TenantSwitcher() {
  const profile = useAuthStore((s) => s.profile);
  const { data: memberships } = useMyTenants();
  const switchT = useSwitchTenant();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  // Close on outside click — sidebar lives inside a scrollable region,
  // so binding to document keeps the dismiss behavior consistent.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const currentName = profile?.tenant_name ?? profile?.tenant_slug ?? '—';
  const list = memberships ?? [];

  async function handleSwitch(tenantId: string) {
    if (tenantId === profile?.tenant_id) { setOpen(false); return; }
    try {
      await switchT.mutateAsync(tenantId);
      setOpen(false);
      toast.success('สลับบริษัทแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สลับบริษัทไม่สำเร็จ');
    }
  }

  function handleAddCompany() {
    setOpen(false);
    // การผูกบริษัทใหม่ต้องยืนยันตัวตนผ่าน LINE (idToken) — นอก LINE ทำไม่ได้
    if (!isInLineClient()) {
      toast.error('เพิ่มบริษัทผ่านการจับคู่ ต้องเปิดในแอป LINE');
      return;
    }
    navigate('/pair?add=1');
  }

  return (
    <div className="relative mt-2" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={switchT.isPending}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-2.5 py-1.5 text-left disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="text-xs font-medium text-gray-700 truncate">{currentName}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden"
        >
          {list.map((m) => {
            const active = m.tenant_id === profile?.tenant_id;
            return (
              <button
                key={m.tenant_id}
                role="option"
                aria-selected={active}
                onClick={() => handleSwitch(m.tenant_id)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 text-left text-xs hover:bg-gray-50 ${
                  active ? 'bg-brand-50' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{m.tenant_name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{m.role} · {m.tenant_code}</div>
                </div>
                {active && <Check className="w-3.5 h-3.5 text-brand-600 shrink-0" />}
              </button>
            );
          })}
          {/* ผูกบริษัทเพิ่มด้วยรหัสจับคู่ — เข้าถึงได้แม้มีบริษัทเดียว */}
          <button
            type="button"
            onClick={handleAddCompany}
            className="w-full flex items-center gap-2 px-2.5 py-2 text-left text-xs text-brand-700 hover:bg-brand-50 border-t border-gray-100"
          >
            <Plus className="w-3.5 h-3.5 shrink-0" />
            <span className="font-medium">เพิ่มบริษัท (ใส่รหัสจับคู่)</span>
          </button>
        </div>
      )}
    </div>
  );
}
